import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import Log from 'utils/log';
import sdkClient from 'utils/sdk-client';
import LocalEvent from 'utils/local-event';
import { addConfirmationDialog } from 'utils/dialogs-manager';
import CookiesUtil from 'utils/cookies-util';
import t from 'utils/translate';
import ConfigIniUtil from '../../../utils/config-ini-util';

const initialState = {
    dataLoaded: false,
    /**
     * This has some default value to prevent errors when the state is used byt the data was not yet loaded.
     * @type {CompanySettings}
     */
    companySettings: {
        CallForwardingSettings: [],
        OperationalStatusList: [],
        AllowedOperationalStatusList: [],
    },
    /**
     * @type {CoreConfigurationSettings}
     */
    coreConfigurationSettings: {},
    organizationId: null,
    organizationLogo: {
        url: '',
        dataIsLoading: true,
        timestamp: 0,
    },
    /**
     * This member can contain only members that have special treatment, like the list of operational statuses sorted
     * which, when used as a selector over array OperationalStatusList will trigger "returned a different result when
     * called with the same parameters. This can lead to unnecessary renders."
     */
    computedMembers: {
        operationalStatusListSortedByCode: [],
        emergencyAlertData: {
            call: {
                enabled: true,
                pending: false,
            },
            message: {
                enabled: true,
                pending: false,
            },
        },
    },
    cacheClearWarning: false,
};

class CompanySettingsSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!CompanySettingsSliceHandler.instance) {
            CompanySettingsSliceHandler.instance = new CompanySettingsSliceHandler();
        }

        return CompanySettingsSliceHandler.instance;
    }

    constructor() {
        super('companySettingsSlice');

        sdkClient.setSocketEventListener('ORGANIZATION_DATA_RESPONSE', (cugData) => this.parseCugData(cugData));
        sdkClient.setSocketEventListener('ORGANIZATION_DATA_UPDATE', (cugData) => this.parseCugData(cugData));

        sdkClient.setEventManagerListener('SDK_SOCKET_CONNECT', () => this.onSocketConnected());

        LocalEvent.setListener(LocalEvent.eventTypes.SDK_CLIENT_AUTHENTICATED, (e) => {
            this.onSdkClientAuthenticated(e.detail[0]);
        });
        LocalEvent.setListener(LocalEvent.eventTypes.USER_SETTING_UPDATED, (e) => {
            this.onUserSettingUpdated(e.detail[0]);
        });

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                /**
                 * Set the company settings data.
                 * This is called on first load, or when a CUG_SETTINGS (RARE or FREQUENT) is received.
                 * @param {object}
                 * @param {{payload: CompanySettings}} action
                 */
                setSettingsDataAction(state, action) {
                    state.companySettings = { ...state.companySettings, ...action.payload };

                    const compareByCode = (a, b) => a.Code.localeCompare(b.Code);

                    if (typeof action.payload.OperationalStatusList !== 'undefined') {
                        state.computedMembers.operationalStatusListSortedByCode =
                            action.payload.OperationalStatusList.sort(compareByCode);
                    }
                },
                /**
                 * Set the core configuration settings data.
                 * This is called on first load only.
                 * @param {object}
                 * @param {{payload: CoreConfigurationSettings}} action
                 */
                setCoreConfigurationSettingsAction(state, action) {
                    state.coreConfigurationSettings = {
                        ...state.coreConfigurationSettings,
                        ...action.payload,
                    };
                },
                setDataLoaded(state, action) {
                    state.dataLoaded = action.payload;
                },
                setOrganizationLogo(state, action) {
                    state.organizationLogo.url = action.payload;
                },
                setOrganizationLogoTimestamp(state, action) {
                    state.organizationLogo.timestamp = action.payload;
                },
                toggleOrganizationLogoLoading(state, action) {
                    state.organizationLogo.dataIsLoading = action.payload;
                },
                setOrganizationId(state, action) {
                    state.organizationId = action.payload;
                },
                setCacheClearWarning(state, action) {
                    state.cacheClearWarning = action.payload;
                },
            },
        });
    }

    onSdkClientAuthenticated(entity) {
        this.dispatch('setOrganizationId', entity.organizationId);
        this.dispatch('setSettingsDataAction', {
            AllowedOperationalStatusList: entity.operationalStatusList,
        });
    }

    loadCoreConfigurationSettings() {
        return new Promise((resolve, reject) => {
            sdkClient.getConfigurationSettings().then(
                /**
                 * @param {CoreConfigurationSettings} configurationSettings
                 */
                (configurationSettings) => {
                    this.dispatch('setCoreConfigurationSettingsAction', configurationSettings);

                    resolve();
                },
                (err) => {
                    Log.error(
                        'COMPANY SETTINGS STORE',
                        `There was an error while loading the core configuration settings: ${JSON.stringify(err)}`,
                    );

                    reject();
                },
            );
        });
    }

    /**
     *
     * @return {CoreConfigurationSettings}
     */
    getCoreConfigurationSettings = () => this.getOwnState().coreConfigurationSettings;

    /**
     * React when user has changed the settings.
     * @param {{name: string, value: any}}settingData
     */
    onUserSettingUpdated = (settingData) => {
        if (settingData.name !== 'call-fwd-settings') {
            return;
        }

        this.dispatch('setSettingsDataAction', {
            CallForwardingSettings: [...settingData.value],
        });
    };

    /**
     * @param {CugData} cugData
     */
    parseCugData(cugData) {
        const currentSettings = this.getOwnState().companySettings;
        /**
         * @type {CompanySettings}
         */
        let companySettings = {};

        if (typeof cugData.CugSettingsRare !== 'undefined') {
            companySettings = { ...companySettings, ...cugData.CugSettingsRare };
        }

        if (typeof cugData.CugSettingsFrequent !== 'undefined') {
            companySettings = { ...companySettings, ...cugData.CugSettingsFrequent };
        }

        if (Object.keys(companySettings).length === 0) {
            return;
        }

        // the call FWD settings in CUG are such a PITA; once those are empty array, once are NULL
        if (
            typeof companySettings.CallForwardingSettings !== 'undefined' &&
            companySettings.CallForwardingSettings === null
        ) {
            companySettings.CallForwardingSettings = [];
        }

        // check if there are things to be notified due to CUG updates
        if (typeof companySettings.CallForwardingSettings !== 'undefined') {
            const callFwdSettingsChanged =
                currentSettings.CallForwardingSettings.length > 0 &&
                !this.callFwdSettingsAreEqual(
                    companySettings.CallForwardingSettings,
                    currentSettings.CallForwardingSettings,
                );

            if (callFwdSettingsChanged) {
                addConfirmationDialog({
                    title: t('settings_call_forward_settings_changed_title'),
                    question: t('settings_call_forward_settings_changed'),
                    yesCallback: () => {
                        LocalEvent.trigger(LocalEvent.eventTypes.CALL_FWD_SETTINGS_UPDATED_EXTERNALLY);
                    },
                    noCallback: () => false,
                });
            }
        }

        this.dispatch('setSettingsDataAction', companySettings);

        /* the OrganizationLogoTimestamp might not be present at all if CugSettingsRare were not included in
        ORGANIZATION_DATA_UPDATE response, in which case it should stay the same */
        if (
            !!companySettings.OrganizationLogoTimestamp &&
            companySettings.OrganizationLogoTimestamp !== this.getOwnState().organizationLogo.timestamp
        ) {
            if (companySettings.OrganizationLogoTimestamp > 0) {
                this.dispatch('toggleOrganizationLogoLoading', true);
                webchatSDK.STWAPI.getCompanyLogo(this.getOwnState().organizationId)
                    .then((pictureData) => {
                        this.dispatchMultiple(
                            this.action(
                                'setOrganizationLogo',
                                `data:${pictureData.data.results.type};base64, ${pictureData.data.results.content}`,
                            ),
                            this.action('setOrganizationLogoTimestamp', companySettings.OrganizationLogoTimestamp),
                        );
                    })
                    .catch((...errorData) => {
                        Log.error('COMPANY SETTINGS STORE', 'There was an error while loading the company logo.');
                        console.log(errorData);
                    })
                    .finally(() => {
                        this.dispatch('toggleOrganizationLogoLoading', false);
                    });
            } else {
                // no logo
                this.dispatchMultiple(
                    this.action('setOrganizationLogo', ''),
                    this.action('setOrganizationLogoTimestamp', 0),
                );
            }
        }

        setTimeout(() => {
            if (!this.getOwnState().dataLoaded) {
                this.dispatch('setDataLoaded', true);
                LocalEvent.trigger(LocalEvent.eventTypes.COMPANY_SETTINGS_LOADED);
                Log.debug('COMPANY SETTINGS STORE', `Loaded internal data`);
            } else {
                LocalEvent.trigger(LocalEvent.eventTypes.COMPANY_SETTINGS_UPDATED);
                Log.debug('COMPANY SETTINGS STORE', `Updated internal data`);
            }
        });
    }

    /**
     * @return {CompanySettings|*}
     */
    getCompanySettings = () => this.getOwnState().companySettings;

    /**
     * @return {boolean}
     */
    isLargeOrganization = () => this.getOwnState().companySettings.CugProtocolVersion === 3;

    /**
     * Check if two sets of call FWD settings are identical.
     * @param {CallForwardingProfileData[]} s1
     * @param {CallForwardingProfileData[]} s2
     * @return {boolean}
     */
    callFwdSettingsAreEqual = (s1, s2) => {
        let areEqual = true;

        s1.forEach((s1Test) => {
            if (!areEqual) {
                return;
            }

            const s2Test = s2.filter((s2Filter) => s2Filter.reason === s1Test.reason)[0];

            Object.keys(s1Test).forEach((prop) => {
                if (!areEqual) {
                    return;
                }

                areEqual = s1Test[prop] === s2Test[prop];
            });
        });

        return areEqual;
    };

    /**
     * Check if the password implements the requirements set at configuration level.
     * @param {string} password
     * @return {PasswordValidationData}
     */
    passwordMeetsRequirements = (password) => {
        const coreConfiguration = this.getOwnState().coreConfigurationSettings;

        const rules = {
            minCharacters:
                coreConfiguration.minLength !== 0 &&
                new RegExp(`[A-za-z0-9${coreConfiguration.allowedSpecialChars}]{${coreConfiguration.minLength},}`),
            lowercase:
                coreConfiguration.minLatinLowercase !== 0 &&
                new RegExp(`[a-z]{${coreConfiguration.minLatinLowercase},}`),
            uppercase:
                coreConfiguration.minLatinUppercase !== 0 &&
                new RegExp(`[A-Z]{${coreConfiguration.minLatinUppercase},}`),
            digit:
                coreConfiguration.minArabicNumeral !== 0 && new RegExp(`[0-9]{${coreConfiguration.minArabicNumeral},}`),
            special:
                coreConfiguration.minSpecialChars !== 0 &&
                new RegExp(`[${coreConfiguration.allowedSpecialChars}]{${coreConfiguration.minSpecialChars},}`),
        };

        return {
            minLength: rules.minCharacters && rules.minCharacters.test(password),
            minLatinLowercase: rules.lowercase && rules.lowercase.test(password),
            minLatinUppercase: rules.uppercase && rules.uppercase.test(password),
            minArabicNumeral: rules.digit && rules.digit.test(password),
            minSpecialChars: rules.special && rules.special.test(password),
        };
    };

    /**
     * The data (CUG settings) is loaded using the socket listeners for ORGANIZATION_DATA_RESPONSE and ORGANIZATION_DATA_UPDATE
     * But, if even do not occur, or occur before the listeners have been set, the internal data is never loaded.
     * In this case the data loading must be forces through the SWAPI
     */
    forceDataLoading = () => {
        sdkClient.getCugSettings().then((cugSettings) => {
            this.parseCugData(cugSettings);
        });
    };

    /**
     * Get the data related with an operational status, when the operational status code is known.
     * @param {number} operationalStatusId
     * @return {OperationalStatusData|null} NULL is returned when the id was not found locally
     */
    getOperationalStatusById = (operationalStatusId) => {
        let result = null;

        const ownState = this.getOwnState().companySettings;

        if (!ownState.AllowOperationalStatus) {
            return null;
        }

        if (operationalStatusId === 0) {
            return null;
        }

        if (ownState.OperationalStatusList) {
            ownState.OperationalStatusList.forEach((operationalStatus) => {
                if (result !== null) {
                    return;
                }

                if (operationalStatus.OperationalStatusId === operationalStatusId) {
                    result = operationalStatus;
                }
            });
        }

        return result;
    };

    onSocketConnected = () => {
        this.checkCacheClearWarning();
    };

    checkCacheClearWarning = () => {
        ConfigIniUtil.loadData(true).then(() => {
            const cookieName = 'stwv';

            const formatVersion = (version) => (typeof version === 'string' ? version.replace(/\.[A-Z]+/, '') : null);
            const packageVersionConfig = formatVersion(ConfigIniUtil.getParam('version'));
            const packageVersionCookie = formatVersion(CookiesUtil.getCookie(cookieName));
            const cacheClearWarningType = ConfigIniUtil.getParam('client.cache.warningType');

            let shouldShowWarning = false;
            let upgradeType = null;

            const setCookie = () => {
                CookiesUtil.setCookie(cookieName, packageVersionConfig);
            };

            do {
                // nothing to do
                if (cacheClearWarningType === 'none') {
                    Log.debug('COMPANY SETTINGS STORE', 'The warning is disabled by config.');

                    break;
                }

                if (packageVersionCookie === null) {
                    Log.notice(
                        'COMPANY SETTINGS STORE',
                        `The cookie was not found. Will set it with current value ${packageVersionConfig}. No warning.`,
                    );

                    // set the cookie and show the dialog; cookie is not present, then, most likely there is a fresh install, so no warning needed
                    setCookie();

                    break;
                }

                if (packageVersionCookie === packageVersionConfig) {
                    Log.debug(
                        'COMPANY SETTINGS STORE',
                        `The version saved in cookie ${packageVersionCookie} is the same as current version => no warning.`,
                    );

                    // need no dialog; was shown before for this version.
                    break;
                }

                // check if it should be shown
                // the package version should have this format: version.major.minor.patch, Ex: 4.0.45.1698078445
                const cachedData = packageVersionCookie.split('.').map((v) => parseInt(v, 10));

                if (cachedData.length !== 4) {
                    Log.notice(
                        'COMPANY SETTINGS STORE',
                        `Invalid cookie value detected. Will set with the current value ${packageVersionConfig}. No warning.`,
                    );

                    setCookie();

                    break;
                }

                // determine the upgrade type
                const versionData = packageVersionConfig.split('.').map((v) => parseInt(v, 10));

                if (versionData[0] > cachedData[0] || versionData[1] > cachedData[1]) {
                    upgradeType = 'major';
                } else if (versionData[2] > cachedData[2]) {
                    upgradeType = 'minor';
                } else if (versionData[3] > cachedData[3]) {
                    upgradeType = 'patch';
                } else {
                    // invalid case; should never happen
                    Log.notice(
                        'COMPANY SETTINGS STORE',
                        `Invalid cookie value detected. Cannot determine the needed upgrade type.`,
                    );
                    setCookie();

                    break;
                }

                switch (cacheClearWarningType) {
                    case 'patch':
                        // basically any type of upgrade will show it
                        if (['major', 'minor', 'patch'].includes(upgradeType)) {
                            shouldShowWarning = true;
                        }
                        break;

                    case 'minor':
                        if (['major', 'minor'].includes(upgradeType)) {
                            shouldShowWarning = true;
                        }
                        break;

                    case 'major':
                        if (upgradeType === 'major') {
                            shouldShowWarning = true;
                        }
                        break;

                    default:
                        // never reach this
                        break;
                }

                // prevent showing this next time
                setCookie();
            } while (false);

            if (shouldShowWarning) {
                Log.debug(
                    'COMPANY SETTINGS STORE',
                    `Will show the warning because the configured type "${cacheClearWarningType}" matches with current upgrade type "${upgradeType}"`,
                );
            }

            this.dispatch('setCacheClearWarning', shouldShowWarning);
        });
    };

    resetCacheClearWarning = () => {
        this.dispatch('setCacheClearWarning', false);
    };
}

// the handler
export const companySettingsSliceHandler = CompanySettingsSliceHandler.getInstance();
// the selectors to be used in useSelect
/**
 * @param state
 * @return {CompanySettings|*}
 */
export const selectCompanySettings = (state) => state.companySettingsSlice.companySettings;
/**
 * @param state
 * @return {ConfigurationSettings}
 */
export const selectCoreConfigurationSettings = (state) => state.companySettingsSlice.coreConfigurationSettings;
/**
 * @param state
 * @return {boolean|*}
 */
export const selectDataLoaded = (state) => state.companySettingsSlice.dataLoaded;
export const selectOrganizationId = (state) => state.companySettingsSlice.organizationId;
export const selectOrganizationLogo = (state) => state.companySettingsSlice.organizationLogo;
/**
 * @param state
 * @return {OperationalStatusData[]|*}
 */
export const selectOperationalStatusesSortedByCode = (state) =>
    state.companySettingsSlice.computedMembers.operationalStatusListSortedByCode;
/**
 * Get the data associated with the emergency alert system.
 * @param state
 * @return {EmergencyAlertData}
 */
export const selectEmergencyAlertData = (state) => state.companySettingsSlice.computedMembers.emergencyAlertData;
export const selectAllowedOperationalStatusList = (state) =>
    state.companySettingsSlice.companySettings.AllowedOperationalStatusList;
export const selectCacheClearWarning = (state) => state.companySettingsSlice.cacheClearWarning;

export const {
    getCompanySettings,
    isLargeOrganization,
    callFwdSettingsAreEqual,
    passwordMeetsRequirements,
    forceDataLoading,
    getOperationalStatusById,
    resetCacheClearWarning,
} = companySettingsSliceHandler;
